import "./PaymentSection.css";
import React, { useState, useEffect } from "react";
import failureImage from "../../../src/assets/payment-failure.png";
import { useSnackbar } from "../../context/SnackbarContext";

import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
} from "@mui/material";
import ProtectPayment from "../PaymentForm";
import CloseIcon from "@mui/icons-material/Close";

function PaymentSection({ record, handleBackToSearch, onPaymentSuccess }) {
  const [total, setTotal] = useState(0);
  const [items, setItems] = useState([]);
  const [billingData, setBillingData] = useState({});
  const [paymentNote, setPaymentNote] = useState("");
  const [customAmount, setCustomAmount] = useState("");
  const [selectedUser, setSelectedUser] = useState(null);
  // const [isPaymentError, setIsPaymentError] = useState(false);
  const [paymentResponse, setPaymentResponse] = useState(null);
  const [selectedItems, setSelectedItems] = useState(new Set());
  const [isPaymentPopupOpen, setIsPaymentPopupOpen] = useState(false);
  const showSnackbar = useSnackbar();

  useEffect(() => {
    setBillingData(record || {});
  }, [record]);

  useEffect(() => {
    const inspectionBillingData = [
      { name: "Termite Inspection(s)", amount: billingData?.tInsp },
      { name: "Home Inspection(s)", amount: billingData?.pInsp },
      { name: "Roof Inspection(s)", amount: billingData?.rInsp },
      { name: "Sewer Lateral Inspection(s)", amount: billingData?.sInsp },
      {
        name: "Natural Hazard Disclosure Report(s)",
        amount: billingData?.nhdRpt,
      },
      { name: "Termite Repair Work", amount: billingData?.tRepairs },
      { name: "Roof Repair Work", amount: billingData?.rRepairs },
      { name: "Sewer Lateral Repair Work", amount: billingData?.sRepairs },
      {
        name: "HomeGuard Plus+ Inspections and Repair",
        amount: billingData?.hhRepairs,
      },
    ];

    const validItems = inspectionBillingData?.filter((item) => item.amount > 0);
    setItems(validItems);
  }, [billingData]);

  useEffect(() => {
    if (paymentResponse) {
      setIsPaymentPopupOpen(false);
      setSelectedItems(new Set());
      setCustomAmount("");
      setPaymentNote("");
      setTotal(0);
    }
  }, [paymentResponse]);

  const handleItemToggle = (itemName) => {
    setCustomAmount("");
    const newSelected = new Set(selectedItems);
    if (newSelected.has(itemName)) {
      newSelected.delete(itemName);
    } else {
      newSelected.add(itemName);
    }
    setSelectedItems(newSelected);
    calculateTotal(newSelected, "");
  };

  const calculateTotal = (selected, custom) => {
    const itemsTotal = items
      .filter((item) => selected.has(item.name))
      .reduce((sum, item) => sum + item.amount, 0);
    const customValue = custom === "" ? 0 : Number(custom);
    setTotal(Number((itemsTotal + customValue).toFixed(2)));
  };

  const handleCustomAmountChange = (value) => {
    setSelectedItems(new Set());
    if (value === "" || /^\d*\.?\d{0,2}$/.test(value)) {
      if (value === ".") {
        setCustomAmount("0.");
      } else {
        setCustomAmount(value);
      }
      calculateTotal(new Set(), value);
    }
  };

  const handlePayment = () => {
    if (total < 5) return; // matches the backend's $5.00 minimum
    
    // Build selectedInspections object based on what user selected
    const selectedInspections = {};
    
    // Map selected items to inspection fields
    if (selectedItems.has("Termite Inspection(s)")) {
      selectedInspections.tInsp = billingData?.tInsp;
    }
    if (selectedItems.has("Home Inspection(s)")) {
      selectedInspections.pInsp = billingData?.pInsp;
    }
    if (selectedItems.has("Roof Inspection(s)")) {
      selectedInspections.rInsp = billingData?.rInsp;
    }
    if (selectedItems.has("Sewer Lateral Inspection(s)")) {
      selectedInspections.sInsp = billingData?.sInsp;
    }
    if (selectedItems.has("Natural Hazard Disclosure Report(s)")) {
      selectedInspections.nhd = billingData?.nhdRpt;
    }
    if (selectedItems.has("Termite Repair Work")) {
      selectedInspections.tRepairs = billingData?.tRepairs;
    }
    if (selectedItems.has("Roof Repair Work")) {
      selectedInspections.rRepairs = billingData?.rRepairs;
    }
    if (selectedItems.has("Sewer Lateral Repair Work")) {
      selectedInspections.sRepairs = billingData?.sRepairs;
    }
    if (selectedItems.has("HomeGuard Plus+ Inspections and Repair")) {
      selectedInspections.hhRepairs = billingData?.hhRepairs;
    }
    
    // For custom amounts, we could add to unallocated or handle differently
    if (customAmount && Number(customAmount) > 0) {
      selectedInspections.unallocated = Number(customAmount);
    }
    
    setSelectedUser({
      zipcode: billingData?.zipid,
      amount: total.toFixed(2),
      address: billingData?.address,
      city: billingData?.city,
      state: billingData?.state,
      paymentNote: paymentNote,
      rptID: billingData?.rptID,
      selectedInspections: selectedInspections // NEW: Include selected inspection breakdown
    });

    setIsPaymentPopupOpen(true);
  };

  const onPaymentErrorHandler = (e) => {
    console.log("Payment Error", e);
    showSnackbar("Payment tokenization failed. Please try again.", "error");
    setIsPaymentPopupOpen(false);
    // setIsPaymentError(e);
    setSelectedItems(new Set());
    setCustomAmount("");
    setPaymentNote("");
    setTotal(0);
  };

  const handlePaymentPopupclose = (event, reason) => {
    if (reason === "backdropClick") {
      return;
    }
    setIsPaymentPopupOpen(false);
  }

  return (
    <section className="payment-section">
      <h2>Make a Payment</h2>
      <div className="report-details">
        <h3>Report Details</h3>
        <div className="report-info">
          <div className="info-field">
            <label>Report ID</label>
            <span>{billingData?.rptID}</span>
          </div>
          <div className="info-field">
            <label>Order Date</label>
            <span>
              {new Date(billingData?.origOrderDate).toLocaleDateString()}
            </span>
          </div>
          <div className="info-field">
            <label>Address</label>
            <span>{billingData?.address}</span>
          </div>
        </div>
      </div>

      <div className="payment-container">
        <h3>Select items to pay:</h3>
        <div className="payment-items">
          {items.length > 0 ? (
            items.map((item) => (
              <div
                key={item.name}
                className="payment-item"
                onClick={() => handleItemToggle(item.name)}
              >
                <div className="payment-item-left">
                  <input
                    type="checkbox"
                    className="custom-checkbox"
                    checked={selectedItems.has(item.name)}
                    onChange={() => handleItemToggle(item.name)}
                    onClick={(e) => e.stopPropagation()}
                  />
                  <span className="item-name">{item.name}</span>
                </div>
                <span className="item-amount">${item.amount.toFixed(2)}</span>
              </div>
            ))
          ) : (
            <div className="no-balance-message">
              <p className="no-balance-text">No current balance due.</p>
              <div className="custom-payment-instructions">
                <p>If you would like to pay for:</p>
                <ul>
                  <li>Reinspection services</li>
                  <li>Recently ordered work not yet invoiced</li>
                  <li>Upcoming scheduled services</li>
                </ul>
                <p>Please use the custom payment option below:</p>
              </div>
            </div>
          )}
        </div>

        <div className="custom-payment">
          <h3>Other Custom Payment Amount</h3>
          <div className="custom-payment-row">
            <div className="note-container">
              <label htmlFor="paymentNote">Note:</label>
              <textarea
                id="paymentNote"
                className="note-textarea"
                value={paymentNote}
                onChange={(e) => setPaymentNote(e.target.value)}
                placeholder="Enter payment note"
              />
            </div>
            <div className="amount-container">
              <label htmlFor="customAmount">Amount:</label>
              <input
                type="text"
                id="customAmount"
                className="custom-amount-input"
                value={customAmount ? `$${customAmount}` : ""}
                onChange={(e) => handleCustomAmountChange(e.target.value.replace("$", ""))}
                placeholder="$0.00"
              />
            </div>
          </div>
        </div>

        <div className="payment-total">
          Total Amount: ${customAmount || total.toFixed(2)}
        </div>

        {/* Backend enforces a $5.00 minimum (card-testing defense) — surface
            it here so users aren't surprised by a server rejection. */}
        {total > 0 && total < 5 && (
          <div style={{ color: "#d32f2f", fontSize: "14px", textAlign: "right", marginBottom: "8px" }}>
            Minimum online payment is $5.00
          </div>
        )}

        <div className="payment-actions">
          <button
            className="pay-button"
            onClick={handlePayment}
            disabled={total < 5}
            style={{ opacity: total >= 5 ? 1 : 0.5, cursor: total >= 5 ? "pointer" : "not-allowed" }}
          >
            Pay Now
          </button>
        </div>
      </div>

      {/* Payment Popup */}
      <Dialog
        PaperProps={{
          sx: { 
            backgroundColor: "#f8f9fa", 
            padding: "20px", 
            width: "600px",
            maxWidth: "90vw", // Responsive: max 90% of viewport width
            fontFamily: "Arial, sans-serif", 
            fontSize:"20px"
          },
        }}
        open={isPaymentPopupOpen}
        onClose={handlePaymentPopupclose}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            marginBottom: "9px",
            height: "23px",
          }}
        >
          <h3 style={{ padding: "0px", margin: "0px" }}>Secure Payment Checkout</h3>
          <IconButton
            sx={{ padding: "0px", margin: "0px" }}
            onClick={() => setIsPaymentPopupOpen(false)}
          >
            <CloseIcon />
          </IconButton>
        </div>
        <DialogContent sx={{ padding: "0px" }}>
          <ProtectPayment
            userData={selectedUser}
            paymentPopupClose={handlePaymentPopupclose}
            onPaymentResponse={(result) => {
              setPaymentResponse(result);
              // Call the parent callback to show receipt in main page
              onPaymentSuccess(result.result, result.customerInfo, selectedUser);
            }}
            onPaymentError={(e) => onPaymentErrorHandler(e)}
          />
        </DialogContent>
      </Dialog>

      {/* Payment failure dialog - only show if payment failed */}
      {paymentResponse && paymentResponse !== null && !paymentResponse?.result?.approval_code && (
        <Dialog
          open
          onClose={() => {
            setPaymentResponse(null);
          }}
          PaperProps={{
            sx: { 
              backgroundColor: "#f8f9fa",
              maxWidth: "500px",
              width: "100%"
            },
          }}
          maxWidth="md"
        >
          <DialogTitle
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            sx={{ color: "#305496", fontWeight: "600", padding: "15px 20px" }}
          >
            Payment Failed
            <IconButton
              sx={{ padding: "0px", margin: "0px" }}
              onClick={() => {
                setPaymentResponse(null);
              }}
            >
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent sx={{ padding: "0px 20px" }}>
            <Box textAlign="center" sx={{ padding: "20px" }}>
              <img
                alt="Payment Failed"
                src={failureImage}
                style={{ maxWidth: "100px", marginBottom: "20px" }}
              />
              <Typography variant="h6" sx={{ color: "#d32f2f", marginBottom: "10px" }}>
                Payment Failed
              </Typography>
              <Typography>
                Transaction ID: {paymentResponse?.result?.transaction_id}
              </Typography>
              <Typography>
                Message: {paymentResponse?.result?.response_message}
              </Typography>
              <Typography>
                Transaction UUID: {paymentResponse?.result?.transaction_uuid}
              </Typography>
            </Box>
          </DialogContent>
          <DialogActions sx={{ padding: "15px 20px" }}>
            <Button
              variant="contained"
              sx={{ 
                backgroundColor: "#305496",
                color: "#fff",
                "&:hover": { backgroundColor: "#264075" }
              }}
              onClick={() => {
                setPaymentResponse(null);
              }}
            >
              Close
            </Button>
          </DialogActions>
        </Dialog>
      )}
    </section>
  );
}

export default PaymentSection;
